import React, { useRef, useState } from 'react';
import { clsx } from 'clsx';
import { CloudArrowUpIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface UploadFile {
  uid: string;
  name: string;
  url?: string;
  status: 'uploading' | 'done' | 'error';
  percent?: number;
}

interface UploadProps {
  accept?: string;
  multiple?: boolean;
  maxCount?: number;
  beforeUpload?: (file: File) => boolean | Promise<boolean>;
  onChange?: (info: { file: UploadFile; fileList: UploadFile[] }) => void;
  onRemove?: (file: UploadFile) => void;
  fileList?: UploadFile[];
  listType?: 'text' | 'picture' | 'picture-card';
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
}

const Upload: React.FC<UploadProps> = ({
  accept,
  multiple = false,
  maxCount = 1,
  beforeUpload,
  onChange,
  onRemove,
  fileList = [],
  listType = 'text',
  className,
  children,
  disabled = false
}) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return;
    
    const filesArray = Array.from(files);
    const limitedFiles = maxCount ? filesArray.slice(0, maxCount) : filesArray;
    
    for (const file of limitedFiles) {
      const shouldUpload = beforeUpload ? await beforeUpload(file) : true;
      
      if (shouldUpload) {
        const uploadFile: UploadFile = {
          uid: Date.now().toString() + Math.random(),
          name: file.name,
          status: 'uploading',
          percent: 0
        };
        
        // Simulate upload progress
        const simulateUpload = () => {
          let progress = 0;
          const interval = setInterval(() => {
            progress += Math.random() * 30;
            if (progress >= 100) {
              progress = 100;
              clearInterval(interval);
              
              // Create object URL for image preview
              if (file.type.startsWith('image/')) {
                uploadFile.url = URL.createObjectURL(file);
              }
              
              uploadFile.status = 'done';
              uploadFile.percent = 100;
            } else {
              uploadFile.percent = progress;
            }
            
            onChange?.({
              file: uploadFile,
              fileList: [...fileList, uploadFile]
            });
          }, 100);
        };
        
        simulateUpload();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input value to allow re-selecting the same file
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (!disabled) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const triggerFileSelect = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleRemove = (file: UploadFile) => {
    onRemove?.(file);
    if (file.url?.startsWith('blob:')) {
      URL.revokeObjectURL(file.url);
    }
  };

  const renderFileList = () => {
    if (listType === 'picture-card') {
      return (
        <div className="grid grid-cols-3 gap-2">
          {fileList.map((file) => (
            <div key={file.uid} className="relative group">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                {file.url ? (
                  <img 
                    src={file.url} 
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <PhotoIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              
              {file.status === 'uploading' && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                  <div className="text-white text-sm">{Math.round(file.percent || 0)}%</div>
                </div>
              )}
              
              <button
                onClick={() => handleRemove(file)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="mt-2 space-y-1">
        {fileList.map((file) => (
          <div key={file.uid} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span className="text-sm text-gray-700 truncate">{file.name}</span>
            <div className="flex items-center space-x-2">
              {file.status === 'uploading' && (
                <div className="text-xs text-blue-600">{Math.round(file.percent || 0)}%</div>
              )}
              <button
                onClick={() => handleRemove(file)}
                className="text-red-500 hover:text-red-700"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const shouldShowUploadArea = !maxCount || fileList.length < maxCount;

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />
      
      {shouldShowUploadArea && (
        <div
          className={clsx(
            'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
            dragOver 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400',
            disabled && 'cursor-not-allowed opacity-50'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={triggerFileSelect}
        >
          {children || (
            <div className="space-y-2">
              <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
              <div className="text-sm text-gray-600">
                <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
              </div>
              <div className="text-xs text-gray-500">
                {accept || 'Any file type'}
              </div>
            </div>
          )}
        </div>
      )}
      
      {fileList.length > 0 && renderFileList()}
    </div>
  );
};

export default Upload; 